import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { api } from "../../lib/api";
import {
  Calendar, Users, ChevronLeft, ChevronRight, Filter,
  Plus, Clock, MapPin, AlertTriangle, User, CheckCircle,
  XCircle, RefreshCw, Search, Settings, Phone
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Badge } from "../../components/ui/badge";
import CreateShiftModal from "../../components/scheduling/CreateShiftModal";
import ShiftDetailPanel from "../../components/scheduling/ShiftDetailPanel";
import CoverageEventPanel from "../../components/scheduling/CoverageEventPanel";

// Calendar Event Content Component
const CalendarEventContent = ({ arg }) => (
  <div className="p-1 overflow-hidden">
    <div className="font-medium text-xs truncate">{arg.event.title}</div>
    {arg.event.extendedProps.caregiver_name && (
      <div className="text-xs opacity-80 truncate">
        {arg.event.extendedProps.caregiver_name}
      </div>
    )}
  </div>
);

// Color coding for shift statuses
const STATUS_COLORS = {
  scheduled: { bg: "#3B82F6", text: "Scheduled" },
  confirmed: { bg: "#10B981", text: "Confirmed" },
  in_progress: { bg: "#8B5CF6", text: "In Progress" },
  completed: { bg: "#6B7280", text: "Completed" },
  cancelled: { bg: "#EF4444", text: "Cancelled" },
  missed: { bg: "#F97316", text: "Missed" },
  needs_coverage: { bg: "#F59E0B", text: "Needs Coverage" },
};

export default function SchedulerCalendar() {
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("timeGridWeek");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  
  // Filters
  const [filterCaregiver, setFilterCaregiver] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [caregivers, setCaregivers] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Panels
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [coverageEvents, setCoverageEvents] = useState([]);
  const [showCoveragePanel, setShowCoveragePanel] = useState(false);

  // Load events when date range or filters change
  const loadEvents = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
        view: currentView.includes("day") ? "day" : currentView.includes("month") ? "month" : "week",
      });
      
      if (filterCaregiver) params.append("caregiver_id", filterCaregiver);
      if (filterClient) params.append("client_id", filterClient);
      if (filterStatus) params.append("status", filterStatus);
      
      const { data } = await api.get(`/scheduling/calendar/events?${params}`);
      setEvents(data.events);
    } catch (err) {
      console.error("Failed to load events:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, currentView, filterCaregiver, filterClient, filterStatus]);

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [caregiversRes, clientsRes] = await Promise.all([
          api.get("/scheduling/calendar/resources?resource_type=caregivers"),
          api.get("/scheduling/calendar/resources?resource_type=clients"),
        ]);
        setCaregivers(caregiversRes.data.resources);
        setClients(clientsRes.data.resources);
      } catch (err) {
        console.error("Failed to load filters:", err);
      }
    };
    loadFilters();
  }, []);

  // Load coverage events
  const loadCoverageEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/scheduling/coverage-events?status=needs_coverage");
      setCoverageEvents(data);
    } catch (err) {
      console.error("Failed to load coverage events:", err);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadCoverageEvents();
  }, [loadEvents, loadCoverageEvents]);

  // Calendar handlers
  const handleDatesSet = (dateInfo) => {
    setDateRange({
      start: dateInfo.startStr.split("T")[0],
      end: dateInfo.endStr.split("T")[0],
    });
  };

  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    if (props.is_open_shift) {
      // Handle open shift click
      setSelectedEvent({ ...props, id: info.event.id, title: info.event.title });
    } else {
      setSelectedEvent({ ...props, id: info.event.id, title: info.event.title });
    }
  };

  const handleDateSelect = (selectInfo) => {
    // Navigate to simplified visit creation with pre-selected date
    const date = selectInfo.startStr.split("T")[0];
    navigate(`/schedule-care?date=${date}`);
  };

  const handleEventDrop = async (info) => {
    const event = info.event;
    const newDate = event.startStr.split("T")[0];
    const newStartTime = event.startStr.split("T")[1]?.substring(0, 5);
    const newEndTime = event.endStr.split("T")[1]?.substring(0, 5);
    
    try {
      await api.put(`/scheduling/visits/${event.extendedProps.visit_id}`, {
        visit_date: newDate,
        start_time: newStartTime,
        end_time: newEndTime,
      });
      loadEvents();
    } catch (err) {
      console.error("Failed to update visit:", err);
      info.revert();
    }
  };

  const calendarApi = calendarRef.current?.getApi();

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-[#06372B]" />
              <h1 className="text-xl font-bold text-gray-900">Scheduler</h1>
            </div>
            
            {/* Navigation */}
            <div className="flex items-center gap-2 ml-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => calendarApi?.prev()}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => calendarApi?.today()}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => calendarApi?.next()}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* View Switcher */}
            <div className="flex items-center gap-1 ml-4">
              {[
                { key: "timeGridDay", label: "Day" },
                { key: "timeGridWeek", label: "Week" },
                { key: "dayGridMonth", label: "Month" },
                { key: "listWeek", label: "List" },
              ].map((view) => (
                <Button
                  key={view.key}
                  variant={currentView === view.key ? "default" : "ghost"}
                  size="sm"
                  onClick={() => {
                    setCurrentView(view.key);
                    calendarApi?.changeView(view.key);
                  }}
                  className={currentView === view.key ? "bg-[#06372B]" : ""}
                >
                  {view.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Coverage Events Badge */}
            {coverageEvents.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setShowCoveragePanel(true)}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                {coverageEvents.length} Need Coverage
              </Button>
            )}

            <Button
              onClick={() => navigate("/schedule-care")}
              className="bg-[#06372B] hover:bg-[#0a4a3a]"
              data-testid="new-visit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Care
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">Filters:</span>
          </div>

          <Select value={filterCaregiver || "all"} onValueChange={(v) => setFilterCaregiver(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Caregivers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Caregivers</SelectItem>
              {caregivers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterClient || "all"} onValueChange={(v) => setFilterClient(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_COLORS).map(([key, val]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: val.bg }} />
                    {val.text}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(filterCaregiver || filterClient || filterStatus) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterCaregiver("");
                setFilterClient("");
                setFilterStatus("");
              }}
            >
              Clear Filters
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={loadEvents}
            className="ml-auto"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Calendar */}
      <div className="flex-1 p-4 overflow-hidden">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full p-4">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={currentView}
            headerToolbar={false}
            events={events}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            select={handleDateSelect}
            eventDrop={handleEventDrop}
            selectable={true}
            editable={true}
            droppable={true}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            slotDuration="00:30:00"
            height="100%"
            nowIndicator={true}
            dayMaxEvents={3}
            eventContent={CalendarEventContent}
          />
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-gray-200 px-6 py-3">
        <div className="flex items-center gap-6">
          <span className="text-sm text-gray-500">Status:</span>
          {Object.entries(STATUS_COLORS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: val.bg }}
              />
              <span className="text-sm text-gray-600">{val.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Shift Detail Panel */}
      <Sheet open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          {selectedEvent && (
            <ShiftDetailPanel
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onUpdate={loadEvents}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Create Shift Modal */}
      {showCreateModal && (
        <CreateShiftModal
          initialDate={createDate}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadEvents();
          }}
        />
      )}

      {/* Coverage Events Panel */}
      <Sheet open={showCoveragePanel} onOpenChange={setShowCoveragePanel}>
        <SheetContent className="w-[500px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Shifts Needing Coverage
            </SheetTitle>
          </SheetHeader>
          <CoverageEventPanel
            events={coverageEvents}
            onAssign={() => {
              loadEvents();
              loadCoverageEvents();
            }}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
